const retry_time = 1000 // milliseconds between attempts at low priority requests

// function the returns a time based promise
const sleep = ms => new Promise(resolve => setTimeout(resolve, ms))

module.exports = {
	/** Creates a request to the server with the requested headers and body
	 * @param {String} path - The server path without the domain. e.g. projects/
	 * @param {String} requestType - the http request type. e.g. GET, POST, SET, DELETE
	 * @param {Object} jsonBody - The body elements to be included with the request
	 * @param {Object} ExtraHeaders - Called extra headers because some are set for you, specifically the 
	 * accept content, content type, and authorization are handled for you
	 * @param {Object} appState - if a reference to the app state is provided, unsuccessful auth will
	 * log the user out of the frontend
	 * @returns {Object} This object contains the response from the server, most useful fields are status
	 * and data */
	JSONRequest: async function(path, requestType, jsonBody, ExtraHeaders, appState=null) {

		// Create the headers
		var head = {
			'Accept': 'application/json',
			'Content-Type': 'application/json',
			'Authorization': ``
		}
		head = { ...head, ...ExtraHeaders };

		// Omit cookies unless its the login route
		const cookiePolicy = (path === "auth/login" ? 'include' : 'omit')
		const fetch_request = () => {
			head.Authorization = this.FindJWTCookie() // Get the JWT cookie to use for authentication
			return fetch(`${process.env.REACT_APP_BACKEND_ROUTE}${path}`, { //TODO: Check that this route exists on startup
				method: requestType,
				headers: head,
				// Only include a body if its not a get of head request
				body: (requestType == "GET" || requestType == "HEAD" ? undefined : JSON.stringify(jsonBody)),
				credentials: cookiePolicy // Important for cookies
			})
		}

		try {
			let response = await fetch_request();            
			// If unautherised, attempt a refresh
			if(response.status == 401) {
				await RefreshConnection();

				response = await fetch_request();
			}

			// Deauth the user if it all failed
			if(appState != null && response.status == 401) {
				appState.set({...appState.get, authed: false});
			}

			let data = await response.json();

			return {data: data, status: response.status}
		} catch (err) {
			return {data: err, status: 500};
		}
	},

	/** Creates a request to the server with the requested headers and body, Low priority because
	 * it will not attempt to refresh the frontends authorization, instead it will just try again repeatedly
	 * hoping that another function refreshed the authentication
	 * @param {String} path - The server path without the domain. e.g. projects/
	 * @param {String} requestType - the http request type. e.g. GET, POST, SET, DELETE
	 * @param {Object} jsonBody - The body elements to be included with the request
	 * @param {Object} ExtraHeaders - Called extra headers because some are set for you, specifically the 
	 * accept content, content type, and authorization are handled for you
	 * @param {Number} retrys - The amount of times the request will retry before failing, 1 second
	 * between retrys
	 * @returns {Object} This object contains the response from the server, most useful fields are status
	 * and data */
	LowPriorityJSONRequest: async function(path, requestType, jsonBody, ExtraHeaders, retrys=5) {
		//TODO: instead of repeatedly retrying a request, it would be better to monitor the JWT token
		// and retry when it changes, or if it doesn't change after a certain time period, then refresh
		// the token

		// Create the headers
		var head = {
			'Accept': 'application/json',
			'Content-Type': 'application/json',
			'Authorization': ``
		}
		head = { ...head, ...ExtraHeaders };

		// Omit cookies unless its the login route
		const cookiePolicy = (path === "auth/login" ? 'include' : 'omit')
		const fetch_request = (jwt) => {
			head.Authorization = jwt;// Get the JWT cookie to use for authentication
			return fetch(`${process.env.REACT_APP_BACKEND_ROUTE}${path}`, { //TODO: Check that this route exists on startup
				method: requestType,
				headers: head,
				// Only include a body if its not a get of head request
				body: (requestType == "GET" || requestType == "HEAD" ? undefined : JSON.stringify(jsonBody)),
				credentials: cookiePolicy // Important for cookies
			})
		}

		try {
			let response = null;
			for(let i = 0; i < retrys; i++) {
				response = await fetch_request(this.FindJWTCookie());

				// If any response that isn't unauthorised returned, stop retrying
				if(response.status != 401) {
					break;
				}

				await sleep(retry_time)
			}

			let data = await response.json();

			return {data: data, status: response.status}
		} catch (err) {
			return {data: err, status: 500};
		}
	},

	/** Gets the current JWT from the cookies
	 * @returns {String} Either the JWT in string form or "No_Token" if it didn't find one
	 */
	FindJWTCookie: function() {
		const match = document.cookie.match(new RegExp(`(?:^|;\\s*)jwt=([^;]+)`));
		return match ? `Bearer ${match[1]}` : "No_Token";
	},

	GetAuth: async function() {
		// Get the current jwt
		const jwt = this.FindJWTCookie()
		
		// Check if the current jwt still functions
		const authCheck = await fetch(`${process.env.REACT_APP_BACKEND_ROUTE}auth`, { //TODO: Check that this route exists on startup
			method: "GET",
			headers: {
				'Accept': 'application/json',
				'Content-Type': 'application/json',
				'Authorization': `${jwt}`
			},
			credentials: 'omit' // its just an auth check, no cookies needed
		})

		if(authCheck.status == 200) {
			return {authed: true, username: authCheck.username}
		}

		// Attempt to refresh the connection using the refresh token
		if((await RefreshConnection()).status == 200) {
			return {authed: true, username: "TODO"}
		}

		// Both tokens either dont exist or are expired
		return {authed: false, username: ""}
	}
}

async function RefreshConnection() {
	try {
		let response = await fetch(`${process.env.REACT_APP_BACKEND_ROUTE}auth/refresh`, {
			method: 'GET',
			headers: {
				'Accept': 'application/json',
			},
			credentials: 'include' // Includes the refresh token cookie
		})
		let data = await response.json();
		let status = response.status;

		return {data: data, status: status}
	} catch (err) {
		return {data: err, status: 500};
	}
}